import { z } from "zod";

/**
 * Ținta unei acțiuni, rezolvată de SDK în 4 trepte:
 * idx (harta din snapshot-ul curent) → elementId (lookup în configul publicat)
 * → selector → fallbackText.
 *
 * `idx` e forma preferată de când promptul conține un snapshot al paginii vii:
 * modelul alege un număr din lista de elemente pe care i-am dat-o noi, deci nu
 * mai poate inventa un selector. Formele vechi rămân valide — un rollback de
 * backend sau un SDK vechi din cache-ul browserului trebuie să meargă în
 * continuare — dar promptul nu le mai cere.
 */
export const targetSchema = z
  .object({
    idx: z.number().int().min(0).optional(),
    elementId: z.string().max(200).optional(),
    selector: z.string().max(500).optional(),
    fallbackText: z.string().max(200).optional(),
  })
  .refine((t) => t.idx !== undefined || t.elementId || t.selector || t.fallbackText, {
    message: "target requires at least one of idx, elementId, selector, fallbackText",
  });

export const highlightActionSchema = z.object({
  type: z.literal("highlight"),
  target: targetSchema,
  title: z.string().max(200).optional(),
  description: z.string().max(1000).optional(),
});

export const tooltipActionSchema = z.object({
  type: z.literal("tooltip"),
  target: targetSchema,
  text: z.string().max(500),
});

export const scrollToActionSchema = z.object({
  type: z.literal("scrollTo"),
  target: targetSchema,
});

export const waitForElementActionSchema = z.object({
  type: z.literal("waitForElement"),
  target: targetSchema,
  timeoutMs: z.number().int().min(0).max(15000).default(5000),
});

export const askUserActionSchema = z.object({
  type: z.literal("askUser"),
  question: z.string().max(500),
});

export const completeTaskActionSchema = z.object({
  type: z.literal("completeTask"),
  summary: z.string().max(500).optional(),
});

/**
 * Navigare programatică - REINTRODUSĂ pe 8 aug 2026, doar pentru modul de
 * voce (decizie de POC, la cererea lui Radu; retrasă pe calea text pe 19 iul).
 * În conversație vocală, „mergi tu acolo și arată-mi" e gestul natural - nu
 * există un link evidențiat pe care vizitatorul să apese cât timp vorbește.
 *
 * `path` e STRICT o cale relativă same-origin („/facturi", „#/pipeline",
 * „/app#/rapoarte"): niciun URL absolut, deci modelul nu poate trimite
 * vizitatorul în afara sitului. Promptul o cere DOAR în segmentul de voce;
 * pe calea text schema o refuză în continuare (vezi llmAgentResponseSchema).
 */
export const navigateActionSchema = z.object({
  type: z.literal("navigate"),
  path: z
    .string()
    .min(1)
    .max(500)
    .refine((p) => p.startsWith("/") || p.startsWith("#"), {
      message: "navigate path must be a same-origin relative path",
    })
    .refine((p) => !p.startsWith("//"), { message: "protocol-relative URLs are not paths" }),
});

/**
 * Uniune discriminată strictă: orice tip de acțiune necunoscut este respins
 * la validare, deci acțiuni noi (ex. click/fill experimental) se adaugă doar
 * explicit, aici.
 */
export const actionSchema = z.discriminatedUnion("type", [
  highlightActionSchema,
  tooltipActionSchema,
  scrollToActionSchema,
  waitForElementActionSchema,
  askUserActionSchema,
  // Navigarea programatică a fost retrasă de AICI pe 19 iul 2026: pe calea
  // text SDK-ul nu duce vizitatorul nicăieri singur - el navighează urmând
  // spotlight-ul (guide.ts, pendingContinuation). NU adăuga navigateActionSchema
  // în uniunea asta: schema intră în structured output-ul căii text, deci ar
  // RECLAMA acțiunea modelului și ar strica byte-identitatea prompt cache-ului.
  // Vocea folosește voiceActionSchema (mai jos), iar calea text a unui app
  // COMMERCE folosește commerceTextActionSchema - din 27 aug 2026 singurele
  // două locuri unde navigate există.
  completeTaskActionSchema,
]);

/**
 * Superset-ul căii de VOCE: tot ce știe calea text, plus `navigate`.
 * În conversație vocală „mergi tu acolo și arată-mi" e gestul natural - nu
 * există un link evidențiat pe care vizitatorul să apese cât timp vorbește.
 * Validat în backend (voiceSplit) și executat în SDK; calea text nu-l vede.
 */
export const voiceActionSchema = z.discriminatedUnion("type", [
  highlightActionSchema,
  tooltipActionSchema,
  scrollToActionSchema,
  waitForElementActionSchema,
  askUserActionSchema,
  navigateActionSchema,
  completeTaskActionSchema,
]);

/**
 * Acțiunile COMMERCE (8 aug 2026, pivotul e-commerce — docs/plan-commerce-actions.md).
 * Active DOAR pe appuri cu `commerce_enabled`; uniunile de bază de mai sus rămân
 * byte-identice pentru toate celelalte appuri (prompt-cache-ul căii text).
 *
 * Principiul anti-halucinare, același ca la `idx`: modelul emite DOAR
 * `variantId`-uri alese din slice-ul de catalog primit în prompt. Ce vede
 * vizitatorul pe card (titlu, preț, imagine) NU vine de la model — backendul
 * îmbogățește acțiunea din catalog (vezi `productCardSchema`), deci un preț
 * inventat nu are pe unde să ajungă pe ecran.
 */
export const showProductsActionSchema = z.object({
  type: z.literal("show_products"),
  items: z
    .array(
      z.object({
        variantId: z.string().min(1).max(64),
        recommended: z.boolean().optional(),
        /** O justificare scurtă, afișată pe card („2× 5.5 Ah incluși"). */
        note: z.string().max(200).optional(),
      }),
    )
    .min(1)
    // CINCI, nu trei: promptul cere pe treapta 1 „3 to 5 fitting products", iar
    // o poartă la 3 transforma al patrulea card într-un schema_mismatch de trei
    // ori la rând și un fallback „nu am înțeles" peste o listă perfect bună
    // (bucla de calibrare, 21 aug 2026: a1/f1/f2/c1 „tura 1 n-a arătat nicio
    // listă" aveau toate cauza asta). Aspirația și poarta sunt lucruri
    // diferite - vezi și nota de la `perspectives` mai jos.
    .max(5),
});

/**
 * `title` e o DOVADĂ DE LEGĂTURĂ, nu un parametru de execuție (10 aug 2026,
 * eșec live pe demo3): garanția de până acum spunea doar că `variantId` EXISTĂ
 * în catalog — nimic nu spunea că e id-ul produsului despre care vorbește
 * modelul. Agentul a spus „am adăugat chinga de transport" și a emis id-ul
 * genții de scule Milwaukee (două titluri asemănătoare, ambele vizibile în
 * slice). Vizitatorul aude una, coșul primește alta.
 *
 * Deci modelul trebuie să COPIEZE titlul produsului pe care crede că îl adaugă,
 * iar backendul compară titlul ăsta cu titlul din catalog al id-ului emis
 * (`enrichCommerceActions`). Nepotrivire ⇒ acțiunea cade, iar tura următoare
 * primește o notă de recuperare. Câmpul NU pleacă spre SDK: e consumat integral
 * de validare (vezi `addToCartServedSchema`).
 */
const boundTitle = z.string().min(1).max(200);

/** Doar după cererea/confirmarea explicită a vizitatorului — regulă de prompt. */
export const addToCartActionSchema = z.object({
  type: z.literal("add_to_cart"),
  variantId: z.string().min(1).max(64),
  /** Titlul EXACT al produsului, copiat din slice-ul de catalog. Vezi `boundTitle`. */
  title: boundTitle,
  quantity: z.number().int().min(1).max(99).default(1),
});

export const setCartQuantityActionSchema = z.object({
  type: z.literal("set_cart_quantity"),
  variantId: z.string().min(1).max(64),
  /** Același contract de legătură ca la `add_to_cart`. */
  title: boundTitle,
  /** 0 = scoate produsul din coș. */
  quantity: z.number().int().min(0).max(99),
});

/**
 * Aceleași acțiuni, așa cum pleacă spre SDK: FĂRĂ `title`.
 *
 * Titlul e o dovadă pentru server, nu un parametru pentru adaptor — SDK-ul
 * execută pe `variantId`, exact ca înainte de 10 aug 2026. Îl tăiem în backend
 * (`enrichCommerceActions`), iar formele astea sunt ce validează atât răspunsul
 * servit, cât și sârma din SDK (`sdk/src/commerce/wire.ts`). Consecința care
 * contează: un SDK vechi din cache-ul browserului vede EXACT acțiunea de azi,
 * iar un SDK nou cu un backend rollback nu pierde nimic (câmpul lipsea oricum).
 */
export const addToCartServedSchema = addToCartActionSchema.omit({ title: true });
export const setCartQuantityServedSchema = setCartQuantityActionSchema.omit({ title: true });

/** SDK-ul duce vizitatorul la coș (`CommerceAdapter.cartPath()`); agentul se oprește acolo. */
export const openCheckoutActionSchema = z.object({
  type: z.literal("open_checkout"),
});

/**
 * Deschide căutarea MAGAZINULUI cu interogarea gata scrisă (9 aug 2026, cerere
 * Radu: „să poată folosi search-ul"). Nu simulăm tastarea în bara temei -
 * căutarea Shopify e condusă prin URL (`/search?q=...`), deci SDK-ul doar
 * navighează la calea construită de adaptor din `query`. Modelul dă DOAR textul
 * interogării; URL-ul îl compune adaptorul (encodeURIComponent), deci nu există
 * cale de a injecta altă destinație. Pe voce urmează protocolul în doi timpi al
 * navigării: anunț → pagina de rezultate → analiza pe snapshotul nou.
 */
export const openSearchActionSchema = z.object({
  type: z.literal("open_search"),
  query: z.string().min(1).max(120),
  /**
   * Bugetul rostit de vizitator („până în cinci sute de lei"), dus în URL-ul de
   * rezultate ca filtru REAL al magazinului (Shopify: `filter.v.price.gte` /
   * `filter.v.price.lte` pe `/search`) - 9 aug 2026, după o conversație live în
   * care agentul a promis de două ori „deschid filtrele de preț" fără să existe
   * vreo acțiune care s-o facă.
   *
   * Unitatea e moneda ÎNTREAGĂ (lei), nu bani: exact ce cere Shopify în URL și
   * exact numărul pe care îl rostește omul. Întregi, deci nicio zecimală de
   * rotunjit; plafonul e o gardă de bun-simț, nu o regulă de magazin.
   *
   * Deliberat FĂRĂ `.refine` pentru „max >= min": schema e o opțiune de
   * `z.discriminatedUnion`, care cere ZodObject pur - un refine ar transforma-o
   * în ZodEffects și ar rupe uniunea. Intervalul inversat se curăță în backend
   * (`enrichCommerceActions`), unde oricum se curăță și restul acțiunilor.
   */
  minPrice: z.number().int().min(0).max(1_000_000).optional(),
  maxPrice: z.number().int().min(0).max(1_000_000).optional(),
});

/**
 * Cererea de FIȘĂ TEHNICĂ, singura acțiune care nu pleacă niciodată spre SDK
 * (11 aug 2026, faza 2 a comparației).
 *
 * Slice-ul de catalog din prompt e o listă compactă — id, titlu, preț, stoc,
 * cale — fiindcă 20 de produse cu descrieri întregi ar umple tot bugetul de
 * tokeni al turei. Pentru „care e diferența dintre X și Y" e prea puțin: fără
 * descriere și fără specificații, singurul lucru pe care modelul îl poate
 * compara e prețul, iar restul l-ar INVENTA. Deci întreabă, explicit.
 *
 * Serverul o interceptează ca pe o unealtă: o taie din acțiuni, aduce
 * detaliile din catalog și RELUEAZĂ tura cu ele în prompt (agent.ts pe text,
 * agentStream.ts pe voce). Cel mult o dată per tură — o a doua cerere e
 * ignorată, deci bucla nu are cum să se închidă.
 */
export const requestDetailsActionSchema = z.object({
  type: z.literal("request_details"),
  variantIds: z.array(z.string().min(1).max(64)).min(2).max(3),
});

/**
 * Declarația STRUCTURATĂ a unei întrebări puse vizitatorului (12 aug 2026,
 * „pendingInteraction"): când agentul întreabă „îl adaug?" sau „pe care îl
 * vrei?", emite și acțiunea asta, cu produsele în joc. Serverul o interceptează
 * ca pe `request_details` - nu ajunge niciodată la SDK - și ține minte
 * întrebarea deschisă, ca „da"-ul sec din tura următoare să aibă un referent
 * MECANIC, nu ghicit din istoric. Clasele reparate: „da" legat de mandatul
 * vechi de adăugare (sesiunile fb4f7f60/263e2a51), confirmarea re-executată.
 *
 * `confirm_remove` (27 aug 2026, sesiunea aee37cd6): întrebarea simetrică de la
 * SCOATEREA din coș - „îl scot?". Există fiindcă ștergerea e de-acum
 * confirm-first structural (vezi garda din enrichCommerceActions): fără un fel
 * dedicat, „da"-ul de după întrebarea de scoatere n-ar purta niciun mandat
 * mecanic și garda ar arunca exact ștergerea cerută.
 */
export const awaitAnswerActionSchema = z.object({
  type: z.literal("await_answer"),
  kind: z.enum(["confirm_add", "choose_product", "confirm_remove"]),
  variantIds: z.array(z.string().min(1).max(64)).min(1).max(5),
});
export type AwaitAnswerAction = z.infer<typeof awaitAnswerActionSchema>;

/**
 * Comparația OMENEASCĂ (11 aug 2026, cerere Radu: „știu că așa ceva nu există,
 * există doar tehnic").
 *
 * Un tabel de specificații nu decide nimic pentru cineva care nu știe ce
 * înseamnă 18V sau 3,5 kg. Perspectivele sunt de-aia ale MODELULUI, în limba
 * vizitatorului („Pentru cine", „Ce proiect i se potrivește", „Confort la
 * lucru", „Ce primești pentru diferența de preț"): o judecată peste fapte, nu
 * o coloană dintr-un export de magazin.
 *
 * Granița dintre ce compune modelul și ce compune serverul e aceeași ca la
 * `show_products`, doar mutată cu un pas: TEXTELE (etichete, notițe, verdict)
 * sunt judecată și vin de la model; CIFRELE de pe ecran (preț, imagine, titlu,
 * cale) le pune backendul din catalog, iar faptele pe care se sprijină judecata
 * trebuie să fi fost primite în prompt (vezi `request_details`).
 *
 * `title` are exact același rol ca la `add_to_cart`: dovadă că id-ul emis e al
 * produsului despre care vorbește modelul (vezi `boundTitle`). O comparație cu
 * o coloană greșită e mai rea decât nicio comparație — vizitatorul citește
 * despre un produs și cumpără altul.
 */
export const showComparisonActionSchema = z.object({
  type: z.literal("show_comparison"),
  items: z
    .array(
      z.object({
        variantId: z.string().min(1).max(64),
        /** Titlul EXACT al produsului, copiat din detaliile primite. Vezi `boundTitle`. */
        title: boundTitle,
      }),
    )
    .min(2)
    .max(3),
  perspectives: z
    .array(
      z.object({
        /** Formularea proprie a modelului, în limba vizitatorului („Pentru cine"). */
        label: z.string().min(1).max(80),
        /** Cine câștigă rândul ăsta, dacă are un câștigător („Bosch, clar"). */
        verdict: z.string().max(120).optional(),
        /** Câte o frază per produs, legată de id-ul lui. */
        notes: z
          .array(
            z.object({
              variantId: z.string().min(1).max(64),
              text: z.string().min(1).max(300),
            }),
          )
          .min(1)
          .max(3),
      }),
    )
    // DOUĂ, nu trei, deși promptul cere în continuare trei-șase. Aspirația și
    // poarta sunt lucruri diferite: pe 11 aug 2026, pe voce, un show_comparison
    // complet și bine format (două coloane, verdict, note legate) a fost aruncat
    // pentru `perspectives: too_small` cu exact două rânduri, iar vizitatorul a
    // auzit „ți le arăt" peste un ecran gol, la nesfârșit. Un tabel cu două
    // rânduri ARĂTAT bate unul perfect aruncat, iar sub două nu mai e un tabel -
    // e același prag ca în schema îmbogățită (`showComparisonEnrichedSchema`) și
    // ca `MIN_COMPARISON_PERSPECTIVES`, deliberat aliniate toate trei.
    .min(2)
    .max(6),
  /** Propoziția „dacă ești X, ia A" - o singură recomandare cinstită. */
  verdict: z.string().min(1).max(300),
});

/**
 * Căutarea în catalog ca UNEALTĂ a modelului (23 aug 2026, pasul 4 din
 * `docs/triage/analiza-mod-agentic-2026-08-22.md`): felia de catalog e un
 * subset lexical al întrebării, iar când vizitatorul cere ceva pe care felia
 * nu-l are modelul nu are de unde să știe dacă produsul lipsește din magazin
 * sau doar din felie. `lookup` îi dă voie să întrebe: serverul o interceptează
 * ca pe `request_details` - nu ajunge niciodată la SDK -, rulează aceeași
 * căutare din care se face felia și RELUEAZĂ tura cu rezultatele în prompt.
 * Doar pe calea text: pe voce răspunsul e deja rostit când s-ar putea relua,
 * iar acolo există `open_search`. O singură dată per tură.
 */
export const lookupActionSchema = z.object({
  type: z.literal("lookup"),
  /** 2-4 cuvinte din vocabularul catalogului, nu fraza vizitatorului. */
  query: z.string().min(2).max(80),
});
export type LookupAction = z.infer<typeof lookupActionSchema>;

const COMMERCE_OPTIONS = [
  showProductsActionSchema,
  showComparisonActionSchema,
  requestDetailsActionSchema,
  lookupActionSchema,
  addToCartActionSchema,
  setCartQuantityActionSchema,
  openCheckoutActionSchema,
  openSearchActionSchema,
] as const;

export const commerceActionSchema = z.discriminatedUnion("type", [...COMMERCE_OPTIONS]);

/**
 * Calea TEXT a unui app commerce: schema de azi + acțiunile de coș + `navigate`.
 * Navigarea a intrat pe text pe 27 aug 2026 (decizie Radu): pe un MAGAZIN,
 * chatul nu mai randează selecții de produse - vitrina e situl însuși, deci
 * „îți arăt" înseamnă „te duc acolo", exact ca pe voce. Pe app-urile
 * ne-commerce interdicția din `actionSchema` rămâne întreagă.
 */
export const commerceTextActionSchema = z.discriminatedUnion("type", [
  ...actionSchema.options,
  navigateActionSchema,
  ...COMMERCE_OPTIONS,
]);

/** Calea de VOCE a unui app commerce: superset-ul vocal + acțiunile de coș. */
export const commerceVoiceActionSchema = z.discriminatedUnion("type", [
  ...voiceActionSchema.options,
  ...COMMERCE_OPTIONS,
]);

/**
 * Cardul de produs ÎMBOGĂȚIT de backend din catalog — forma în care
 * `show_products` ajunge la SDK (modelul a emis doar variantId + note).
 * `price`/`compareAtPrice` sunt sume formatate de backend („1.899 lei") ca
 * SDK-ul să nu aibă opinii despre monedă.
 */
export const productCardSchema = z.object({
  variantId: z.string().max(64),
  productId: z.string().max(64),
  title: z.string().max(300),
  /** Numele variantei când produsul are opțiuni („2× 5.5 Ah"), altfel absent. */
  variantTitle: z.string().max(200).optional(),
  price: z.string().max(50),
  compareAtPrice: z.string().max(50).optional(),
  imageUrl: z.string().max(1000).optional(),
  /** Calea relativă a paginii produsului, pentru „Vezi produsul". */
  productPath: z.string().max(500),
  available: z.boolean(),
  recommended: z.boolean().optional(),
  note: z.string().max(200).optional(),
});

/** `show_products` după îmbogățire — ce primește și randează SDK-ul. */
export const showProductsEnrichedSchema = z.object({
  type: z.literal("show_products"),
  items: z.array(productCardSchema).min(1).max(5),
});

/**
 * `show_comparison` după îmbogățire.
 *
 * Coloanele sunt exact `productCardSchema` — aceeași formă ca la
 * `show_products`, deliberat: e același produs, îmbogățit din același catalog,
 * de aceeași funcție (`toCard`). O formă proprie ar fi însemnat un al doilea
 * loc de ținut la zi când catalogul capătă un câmp, pentru zero informație în
 * plus. Ce nu se folosește pe coloană (nota, badge-ul de recomandat) pur și
 * simplu nu se randează.
 *
 * Perspectivele trec NEATINSE prin backend: sunt judecată peste faptele pe care
 * modelul le-a primit, nu date de catalog. Ce se verifică e legătura — un
 * `variantId` care nu e al niciunei coloane cade (vezi enrichCommerceActions).
 */
export const comparisonPerspectiveSchema = z.object({
  label: z.string().max(80),
  verdict: z.string().max(120).optional(),
  notes: z
    .array(z.object({ variantId: z.string().max(64), text: z.string().max(300) }))
    .min(1)
    .max(3),
});

export const showComparisonEnrichedSchema = z.object({
  type: z.literal("show_comparison"),
  items: z.array(productCardSchema).min(2).max(3),
  perspectives: z.array(comparisonPerspectiveSchema).min(2).max(6),
  verdict: z.string().max(300),
});

export type Target = z.infer<typeof targetSchema>;
export type Action = z.infer<typeof actionSchema>;
/** Acțiunile căii vocale: `Action` plus `navigate`. */
export type VoiceAction = z.infer<typeof voiceActionSchema>;
export type CommerceAction = z.infer<typeof commerceActionSchema>;
export type CommerceTextAction = z.infer<typeof commerceTextActionSchema>;
export type CommerceVoiceAction = z.infer<typeof commerceVoiceActionSchema>;
export type ProductCard = z.infer<typeof productCardSchema>;
export type ShowProductsEnriched = z.infer<typeof showProductsEnrichedSchema>;
export type ComparisonPerspective = z.infer<typeof comparisonPerspectiveSchema>;
export type ShowComparisonEnriched = z.infer<typeof showComparisonEnrichedSchema>;

/**
 * Tipurile valide de acțiune, derivate din uniune (nu o listă paralelă care
 * poate rămâne în urmă). Sursa pentru filtrarea tolerantă din backend: un model
 * slab inventează uneori tipuri retrase (ex. "navigate", scos pe 19 iul 2026),
 * iar backend-ul aruncă acea acțiune păstrând restul răspunsului, în loc să
 * arunce tot răspunsul.
 */
export const ACTION_TYPES: ReadonlySet<Action["type"]> = new Set(
  actionSchema.options.map((option) => option.shape.type.value),
);

/**
 * Mesajul pe care SDK-ul îl trimite providerului de voce ca să RELUE tura după
 * ce a executat o navigare și a recapturat ecranul nou.
 *
 * De ce un sentinel și nu o propoziție: textul unui mesaj de utilizator ajunge
 * în prompt ca întrebare, iar regula de limbă („răspunde în limba întrebării")
 * ar face agentul să treacă pe engleză în mijlocul unei conversații în română.
 * Backendul îl recunoaște, îl înlocuiește cu întrebarea ORIGINALĂ a omului și
 * marchează tura drept continuare - vezi routes/voiceLlm.ts.
 */
export const VOICE_CONTINUE_SENTINEL = "@@AIG_CONTINUE@@";
